import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Loader2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "reviewed", "contacted"];
const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).filter(Boolean).join(" ") : "Something went wrong.");

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data?.token);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const cls = "w-full bg-transparent border border-[#262b38] focus:border-[#d96b27] focus:outline-none px-4 py-3";
  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-24 blueprint">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md border border-[#262b38] bg-[#0c0e12] p-8 sm:p-10 space-y-6">
        <Logo />
        <h1 className="font-display text-3xl font-bold">Studio login</h1>
        <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={cls} />
        <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={cls} />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#d96b27] hover:bg-[#f07d38] text-[#0c0e12] py-3 font-mono text-xs uppercase tracking-[0.2em] flex justify-center">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const Row = ({ e, onStatus, onDelete }) => (
  <article data-testid={`enquiry-row-${e.id}`} className="border border-[#262b38] bg-[#0c0e12] p-6 grid lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3">
      <p className="font-display text-xl font-bold">{e.name}</p>
      <a href={`mailto:${e.email}`} className="text-[#d96b27] text-sm break-all">{e.email}</a>
      {e.phone && <p className="text-sm text-[#a7aebb]">{e.phone}</p>}
      <p className="font-mono text-xs text-[#a7aebb] mt-2">{new Date(e.created_at).toLocaleString("en-GB")}</p>
    </div>
    <div className="lg:col-span-6 space-y-3">
      <div className="flex flex-wrap gap-2 font-mono text-xs uppercase tracking-[0.12em]">
        {[e.service, e.budget, e.timeline, e.location].filter(Boolean).map((t) => (
          <span key={t} className="border border-[#262b38] px-2 py-1 text-[#c9ced8]">{t}</span>
        ))}
      </div>
      <p className="text-[#c9ced8] whitespace-pre-line">{e.message}</p>
    </div>
    <div className="lg:col-span-3 flex lg:flex-col lg:items-end gap-3">
      <select data-testid={`enquiry-status-${e.id}`} value={e.status} onChange={(ev) => onStatus(e.id, ev.target.value)} className="bg-[#14171f] border border-[#262b38] px-3 py-2 font-mono text-xs uppercase">
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button data-testid={`enquiry-delete-${e.id}`} onClick={() => onDelete(e.id)} className="flex items-center gap-2 text-sm text-[#a7aebb] hover:text-red-400">
        <Trash2 className="w-4 h-4" /> Delete
      </button>
    </div>
  </article>
);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ib_token"));
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const headers = { Authorization: `Bearer ${token}` };

  const logout = useCallback(() => {
    sessionStorage.removeItem("ib_token");
    setToken(null);
    setItems([]);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => logout());
  }, [token, logout]);

  const login = (t) => {
    if (!t) return;
    sessionStorage.setItem("ib_token", t);
    setToken(t);
  };

  const onStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, { headers });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers });
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    }
  };

  if (!token) return <Login onLogin={login} />;
  const shown = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="admin-page" className="mx-auto max-w-[1440px] px-5 sm:px-10 pt-32 pb-24 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#d96b27]">Studio</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Enquiries <span className="text-[#a7aebb]" data-testid="admin-enquiry-count">({items.length})</span></h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb] hover:text-[#f4f5f7]">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)} className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border ${filter === s ? "bg-[#d96b27] border-[#d96b27] text-[#0c0e12]" : "border-[#262b38] text-[#a7aebb]"}`}>
            {s}
          </button>
        ))}
      </div>
      <div data-testid="admin-enquiries-list" className="space-y-4">
        {shown.length === 0 && <p className="text-[#a7aebb] border border-dashed border-[#262b38] p-10 text-center">No enquiries yet.</p>}
        {shown.map((e) => <Row key={e.id} e={e} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
    </div>
  );
}
